import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { DmsTextField, DmsButton, DmsModal, fetchData, HTTP_STATUS, intl } from 'dmsbasereact';
import { DmsAutocomplete } from '../DmsAutoComplete/DmsAutoComplete';
import {
    Alert,
    Box,
    Grid,
    Typography,
    IconButton,
    Tooltip,
    Divider,
    InputAdornment,
    CircularProgress,
    Button,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { button3DSx } from '../../constants/ButtonStyles';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Swal from 'sweetalert2';

const createEmptyItem = () => ({
    id: Date.now(),
    batchNumber: '',
    productNumber: '',
    quantity: 1,
    batchValid: null,
    productValid: null,
    batchInfo: null,
    productInfo: null,
    validating: false,
});

const CACHE_KEY = 'manualCheckinFormCache';

const loadCache = () => {
    try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        return cached ? JSON.parse(cached) : null;
    } catch { return null; }
};

export const ManualCheckinForm = ({ open = true, onClose = null, initialData = null, onSuccess = null }) => {
    const cached = useRef(initialData || loadCache());
    const [originPk, setOriginPk] = useState(cached.current?.originPk || '');
    const [destinationPk, setDestinationPk] = useState(cached.current?.destinationPk || '');
    const [danfeNumber, setDanfeNumber] = useState(cached.current?.danfeNumber || '');
    const [items, setItems] = useState(cached.current?.items || [createEmptyItem()]);
    const [origins, setOrigins] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [loadingOrigins, setLoadingOrigins] = useState(false);
    const [loadingDestinations, setLoadingDestinations] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [correctionConfirmed, setCorrectionConfirmed] = useState(false);

    useEffect(() => {
        if (onClose) return;
        const data = { originPk, destinationPk, danfeNumber, items };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    }, [originPk, destinationPk, danfeNumber, items, onClose]);

    useEffect(() => {
        const fetchUserInstitutions = async () => {
            setLoadingDestinations(true);
            try {
                const response = await fetchData('/batch_location/checkout/origins', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    const data = response.data || [];
                    setDestinations(data);
                    if (data.length === 1 && !cached.current?.destinationPk) {
                        setDestinationPk(data[0].pk);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch destinations', error);
            } finally {
                setLoadingDestinations(false);
            }
        };
        fetchUserInstitutions();
    }, []);

    useEffect(() => {
        if (!destinationPk) {
            setOrigins([]);
            setOriginPk('');
            return;
        }
        const fetchPartners = async () => {
            setLoadingOrigins(true);
            try {
                const response = await fetchData(`/batch_location/checkout/destinations/${destinationPk}`, 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    setOrigins(response.data || []);
                }
            } catch (error) {
                console.error('Failed to fetch origins', error);
            } finally {
                setLoadingOrigins(false);
            }
        };
        fetchPartners();
    }, [destinationPk]);

    const destinationOptions = destinations.map(d => ({ value: d.pk, label: d.name }));
    const originOptions = origins.map(o => ({ value: o.pk, label: o.name }));

    const validateBatch = useCallback(async (itemId, batchNumber) => {
        if (!batchNumber.trim()) return;

        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, validating: true } : item
        ));

        try {
            const response = await fetchData(
                `/batch_location/checkin/validate-batch?number=${encodeURIComponent(batchNumber.trim())}`,
                'GET', true
            );
            if (response.status === HTTP_STATUS.OK_200 && response.data?.valid) {
                const info = response.data;
                setItems(prev => prev.map(item =>
                    item.id === itemId ? {
                        ...item,
                        batchValid: true,
                        batchInfo: info,
                        productNumber: info.part_number || '',
                        productValid: true,
                        productInfo: { pk: info.part_pk, number: info.part_number, name: info.part_name },
                        validating: false,
                    } : item
                ));
            } else {
                setItems(prev => prev.map(item =>
                    item.id === itemId ? {
                        ...item,
                        batchValid: false,
                        batchInfo: null,
                        productNumber: '',
                        productValid: null,
                        productInfo: null,
                        validating: false,
                    } : item
                ));
            }
        } catch {
            setItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, batchValid: false, batchInfo: null, validating: false } : item
            ));
        }
    }, []);

    const validateProduct = useCallback(async (itemId, productNumber) => {
        if (!productNumber.trim()) return;

        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, validating: true } : item
        ));

        try {
            const response = await fetchData(
                `/batch_location/checkin/validate-product?number=${encodeURIComponent(productNumber.trim())}`,
                'GET', true
            );
            if (response.status === HTTP_STATUS.OK_200 && response.data?.valid) {
                setItems(prev => prev.map(item =>
                    item.id === itemId ? {
                        ...item,
                        productValid: true,
                        productInfo: response.data,
                        validating: false,
                    } : item
                ));
            } else {
                setItems(prev => prev.map(item =>
                    item.id === itemId ? {
                        ...item,
                        productValid: false,
                        productInfo: null,
                        validating: false,
                    } : item
                ));
            }
        } catch {
            setItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, productValid: false, productInfo: null, validating: false } : item
            ));
        }
    }, []);

    const updateItem = useCallback((itemId, field, value) => {
        setItems(prev => prev.map(item => {
            if (item.id !== itemId) return item;
            const updated = { ...item, [field]: value };
            if (field === 'batchNumber') {
                updated.batchValid = null;
                updated.batchInfo = null;
                updated.productNumber = '';
                updated.productValid = null;
                updated.productInfo = null;
            }
            if (field === 'productNumber') {
                updated.productValid = null;
                updated.productInfo = null;
            }
            return updated;
        }));
    }, []);

    const addItem = useCallback(() => {
        setItems(prev => [...prev, createEmptyItem()]);
    }, []);

    const removeItem = useCallback((itemId) => {
        setItems(prev => prev.length > 1 ? prev.filter(i => i.id !== itemId) : prev);
    }, []);

    const resetForm = useCallback(() => {
        setOriginPk('');
        if (destinations.length !== 1) setDestinationPk('');
        setDanfeNumber('');
        setItems([createEmptyItem()]);
        sessionStorage.removeItem(CACHE_KEY);
    }, [destinations]);

    const isCorrection = initialData?.isCorrection || false;

    const canSubmit = originPk && destinationPk &&
        items.length > 0 &&
        items.every(i => i.batchValid && i.productValid && i.quantity > 0) &&
        (!isCorrection || correctionConfirmed) &&
        !submitting;

    const handleSubmit = useCallback(async () => {
        if (!canSubmit) return;

        const result = await Swal.fire({
            title: intl.get('Checkin.ConfirmEntrada').d('Confirm entry'),
            text: intl.get('Checkin.ConfirmMessage').d('Do you want to confirm receipt of this invoice?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: intl.get('Confirm').d('Confirm'),
            cancelButtonText: intl.get('Cancel').d('Cancel'),
        });

        if (!result.isConfirmed) return;

        setSubmitting(true);
        try {
            const payload = {
                origin_pk: originPk,
                destination_pk: destinationPk,
                danfe_number: danfeNumber || null,
                is_correction: initialData?.isCorrection || false,
                original_invoice_pk: initialData?.originalInvoicePk || null,
                items: items.map(i => ({
                    batch_number: i.batchNumber.trim(),
                    product_number: i.productNumber.trim(),
                    quantity: parseInt(i.quantity, 10),
                })),
            };

            const response = await fetchData('/batch_location/checkin/manual', 'POST', true, payload);
            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    title: intl.get('Success').d('Success'),
                    text: intl.get('Checkin.ManualSuccess').d('Manual entry registered successfully!'),
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false,
                });
                resetForm();
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            } else {
                const errorMsg = response.data?.detail || response.data?.message || intl.get('Checkin.ManualError').d('Error registering manual entry');
                Swal.fire({
                    title: intl.get('Error').d('Error'),
                    text: errorMsg,
                    icon: 'error',
                });
            }
        } catch (error) {
            console.error('Error submitting manual checkin:', error);
            Swal.fire({
                title: intl.get('Error').d('Error'),
                text: intl.get('Checkin.ManualError').d('Error registering manual entry'),
                icon: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    }, [canSubmit, originPk, destinationPk, danfeNumber, items, resetForm, initialData]);

    const getValidationAdornment = (valid, validating) => {
        if (validating) return { endAdornment: <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment> };
        if (valid === true) return { endAdornment: <InputAdornment position="end"><CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small" /></InputAdornment> };
        if (valid === false) return { endAdornment: <InputAdornment position="end"><ErrorIcon sx={{ color: 'error.main' }} fontSize="small" /></InputAdornment> };
        return {};
    };

    const formContent = (
        <Box>
            {isCorrection && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {intl.get('Checkin.CorrectionWarning').d('You are correcting an invoice. Enter the correct data below.')}
                </Alert>
            )}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {intl.get('InvoiceDetails').d('Invoice Details')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <DmsAutocomplete
                        label={intl.get('Destination').d('Destination')}
                        value={destinationPk}
                        onChange={(val) => { setDestinationPk(val); setOriginPk(''); }}
                        options={destinationOptions}
                        loading={loadingDestinations}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <DmsAutocomplete
                        label={intl.get('Origin').d('Origin')}
                        value={originPk}
                        onChange={(val) => setOriginPk(val)}
                        options={originOptions}
                        loading={loadingOrigins}
                        disabled={!destinationPk}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <DmsTextField
                        label={intl.get('InvoiceNumber').d('Invoice Number')}
                        value={danfeNumber}
                        onChange={(e) => setDanfeNumber(e.target.value)}
                        fullWidth
                        placeholder={intl.get('EnterInvoiceNumber').d('Enter invoice number')}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {intl.get('InvoiceItems').d('Invoice Items')}
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: '18px !important' }} />}
                    onClick={addItem}
                    sx={button3DSx}
                >
                    {intl.get('Add').d('Add')}
                </Button>
            </Box>

            {items.map((item, index) => (
                <Box key={item.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {`${intl.get('Items').d('Items')} #${index + 1}`}
                        </Typography>
                        {items.length > 1 && (
                            <Tooltip title={intl.get('Remove').d('Remove')}>
                                <IconButton size="small" color="error" onClick={() => removeItem(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <DmsTextField
                                label={intl.get('BatchNumber').d('Batch Number')}
                                value={item.batchNumber}
                                onChange={(e) => updateItem(item.id, 'batchNumber', e.target.value)}
                                onBlur={() => validateBatch(item.id, item.batchNumber)}
                                fullWidth
                                InputProps={getValidationAdornment(item.batchValid, item.validating)}
                                error={item.batchValid === false}
                                helperText={item.batchValid === false ? intl.get('Checkin.BatchNotFound').d('Batch not found') : ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <DmsTextField
                                label={intl.get('Product').d('Product')}
                                value={item.productNumber}
                                onChange={(e) => updateItem(item.id, 'productNumber', e.target.value)}
                                onBlur={() => {
                                    if (!item.batchValid) validateProduct(item.id, item.productNumber);
                                }}
                                fullWidth
                                disabled={item.batchValid === true}
                                InputProps={getValidationAdornment(item.productValid, false)}
                                error={item.productValid === false}
                                helperText={
                                    item.productValid === false
                                        ? intl.get('Checkin.ProductNotFound').d('Product not found')
                                        : item.productInfo?.name || ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <DmsTextField
                                label={intl.get('Quantity').d('Quantity')}
                                type="number"
                                value={item.quantity}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    updateItem(item.id, 'quantity', val === '' ? '' : Math.max(1, parseInt(val, 10) || 1));
                                }}
                                fullWidth
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            ))}

            {isCorrection && (
                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Checkbox
                            checked={correctionConfirmed}
                            onChange={(e) => setCorrectionConfirmed(e.target.checked)}
                        />
                    }
                    label={intl.get('Checkin.CorrectionConfirmCheckbox').d('I confirm that the invoice data has been corrected')}
                />
            )}
        </Box>
    );

    if (!onClose) return formContent;

    return (
        <DmsModal
            isVisible={open}
            onClose={onClose}
            maxWidth="md"
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                    {isCorrection
                        ? intl.get('Checkin.CorrectInvoice').d('Correct Invoice')
                        : intl.get('Checkin.ManualTab').d('Manual Entry')
                    }
                </Typography>
            </Box>
            <DmsModal.Body>
                <Box sx={{ p: 2.5 }}>
                    {formContent}
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, width: '100%' }}>
                    <DmsButton
                        text={intl.get('Cancel').d('Cancel')}
                        onClick={onClose}
                    />
                    <DmsButton
                        onClick={handleSubmit}
                        text={submitting
                            ? intl.get('Processing').d('Processing...')
                            : intl.get('Checkin.SubmitManual').d('Submit Manual Entry')
                        }
                        disabled={!canSubmit}
                        variant="primary"
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

ManualCheckinForm.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    initialData: PropTypes.object,
    onSuccess: PropTypes.func,
};
